import { useEffect, useRef } from "react";

/**
 * A custom React hook that returns the previous value of a variable.
 *
 * @template T The type of the value.
 * @param {T} value The current value.
 * @param {boolean} allowFalsy Whether to allow falsy values to be set as the previous value.
 * @returns {T | undefined} The previous value, or undefined on the first render.
 */
function usePrevious<T>(value: T, allowFalsy = false): T | undefined {
  const ref = useRef<T>();

  // Store current value in ref after rendering, so it's available as previous value on next render
  useEffect(() => {
    // If falsy values are not allowed, and the value is falsy, don't update the previous value
    if (!allowFalsy && !value) {
      return;
    }

    ref.current = value;
  }, [value, allowFalsy]); // Re-run only when value changes

  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

export default usePrevious;
